///
/// EarthBrowser Admin Dashboard - User Management Table
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///
/// Pure search/browse list: find an account, click the row to open its
/// control panel (UserControlPanel), where all per-user actions live.
///

import React from "react"
import {
  useGetAdminUsersQuery,
  useGetRolesQuery,
  type User,
} from "@/redux/api/permissionsApi"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader } from "@/components/ui/loader"
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import {
  type Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table"

function SortableHeader({ column, label }: { column: Column<User, unknown>; label: string }) {
  const sorted = column.getIsSorted()
  return (
    <button
      type="button"
      className="flex items-center gap-1 text-sm font-medium hover:text-foreground transition-colors"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {label}
      {sorted === "asc" ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
      )}
    </button>
  )
}

const columns: ColumnDef<User>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: ({ column }) => <SortableHeader column={column} label="ID" />,
    cell: ({ row }) => (
      <span className="text-xs font-mono">{row.original.id}</span>
    ),
    size: 80,
  },
  {
    id: "email",
    accessorKey: "email",
    header: ({ column }) => <SortableHeader column={column} label="Email" />,
    cell: ({ row }) => (
      <span className="text-xs font-mono">{row.original.email}</span>
    ),
  },
  {
    id: "name",
    accessorFn: (row) => `${row.first_name} ${row.last_name}`,
    header: ({ column }) => <SortableHeader column={column} label="Name" />,
    cell: ({ row }) => (
      <span className="text-xs">
        {row.original.first_name} {row.original.last_name}
      </span>
    ),
  },
  {
    id: "status",
    accessorKey: "status",
    header: ({ column }) => <SortableHeader column={column} label="Status" />,
    cell: ({ row }) => (
      <Badge
        variant={
          row.original.status === "active"
            ? "default"
            : row.original.status === "suspended"
              ? "destructive"
              : "secondary"
        }
        className="rounded-full"
      >
        {row.original.status}
      </Badge>
    ),
    size: 120,
  },
  {
    id: "roles",
    accessorKey: "roles",
    enableSorting: false,
    header: () => <span className="text-sm font-medium">Roles</span>,
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {Array.isArray(row.original.roles) && row.original.roles.length > 0 ? (
          row.original.roles.map((role) => (
            <Badge key={role} variant="outline" className="text-xs rounded-full">
              {role}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </div>
    ),
  },
  {
    id: "last_active",
    accessorKey: "last_active_day",
    header: ({ column }) => <SortableHeader column={column} label="Last Active" />,
    cell: ({ row }) => (
      <span className="text-xs">
        {row.original.last_active_day
          ? new Date(row.original.last_active_day).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
          : "—"}
      </span>
    ),
    size: 110,
  },
  {
    id: "device_count",
    accessorKey: "device_count",
    header: ({ column }) => <SortableHeader column={column} label="Devices" />,
    cell: ({ row }) => (
      <span className="text-xs">{row.original.device_count}</span>
    ),
    size: 90,
  },
  {
    id: "active_sessions",
    accessorKey: "active_sessions",
    header: ({ column }) => <SortableHeader column={column} label="Sessions" />,
    cell: ({ row }) => (
      <span className="text-xs">{row.original.active_sessions}</span>
    ),
    size: 90,
  },
]

export function UserManagementTable({ onSelectUser }: { onSelectUser: (userId: number) => void }) {
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })
  const [search, setSearch] = React.useState("")
  const [statusFilter, setStatusFilter] = React.useState<string>("all")
  const [roleFilter, setRoleFilter] = React.useState<string>("all")
  const [sorting, setSorting] = React.useState<SortingState>([])

  const { data, isLoading, error } = useGetAdminUsersQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    role: roleFilter !== "all" ? roleFilter : undefined,
    sort: sorting[0]?.id,
    dir: sorting[0] ? (sorting[0].desc ? "desc" : "asc") : undefined,
  })

  const { data: rolesData } = useGetRolesQuery()

  const table = useReactTable({
    data: data?.users ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: data?.pagination.pages ?? -1,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting(updater)
      setPagination((p) => ({ ...p, pageIndex: 0 }))
    },
  })

  if (error) {
    const errorMessage =
      "status" in error
        ? `HTTP ${error.status}: ${
            "data" in error && error.data && typeof error.data === "object" && "message" in error.data
              ? String(error.data.message)
              : "Unknown error"
          }`
        : "message" in error
          ? String(error.message)
          : "Unknown error"

    return (
      <div className="p-4 text-center text-red-500">
        <p className="font-semibold">Error loading users</p>
        <p className="text-sm mt-2">{errorMessage}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 flex-wrap">
        <Input
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPagination({ ...pagination, pageIndex: 0 })
          }}
          className="max-w-sm rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
        />
        <Select
          value={statusFilter}
          onValueChange={(value: string) => {
            setStatusFilter(value)
            setPagination({ ...pagination, pageIndex: 0 })
          }}
        >
          <SelectTrigger className="w-[180px] rounded-xl focus:ring-2 focus:ring-blue-500">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all" className="rounded-lg">All Statuses</SelectItem>
            <SelectItem value="active" className="rounded-lg">Active</SelectItem>
            <SelectItem value="suspended" className="rounded-lg">Suspended</SelectItem>
            <SelectItem value="unverified" className="rounded-lg">Unverified</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={roleFilter}
          onValueChange={(value: string) => {
            setRoleFilter(value)
            setPagination({ ...pagination, pageIndex: 0 })
          }}
        >
          <SelectTrigger className="w-[180px] rounded-xl focus:ring-2 focus:ring-blue-500">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all" className="rounded-lg">All Roles</SelectItem>
            {rolesData?.roles.map((role) => (
              <SelectItem key={role.id} value={role.name} className="rounded-lg">
                {role.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <Loader />
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() => onSelectUser(row.original.id)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {data && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                {data.pagination.total === 0
                  ? 0
                  : pagination.pageIndex * pagination.pageSize + 1}{" "}
                to{" "}
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  data.pagination.total
                )}{" "}
                of {data.pagination.total} users
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-2 px-4">
                  <span className="text-sm">
                    Page {pagination.pageIndex + 1} of{" "}
                    {data.pagination.pages || 1}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
